import { useEffect, useState } from "react";
import { onSnapshot, query, collection, where, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { approveFollow, denyFollow } from "@/data/follow";
import { useAuth } from "@/lib/useAuth";
import { toast } from "@/components/ui/use-toast";

type PendingEdge = {
  followerId: string;
  followingId: string;
  edgeId: string;
  createdAt?: any;
};

export default function FollowRequestsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { user } = useAuth();
  const uid = user?.uid;
  const [incoming, setIncoming] = useState<PendingEdge[]>([]);
  const [outgoing, setOutgoing] = useState<PendingEdge[]>([]);
  const [busy, setBusy] = useState<string | null>(null); // edgeId currently acting on
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !uid) return;
    setLoading(true);

    const incomingQ = query(
      collection(db, "follows"),
      where("followingId", "==", uid),
      where("status", "==", "pending"),
      limit(100)
    );
    const outgoingQ = query(
      collection(db, "follows"),
      where("followerId", "==", uid),
      where("status", "==", "pending"),
      limit(100)
    );

    let incomingReady = false;
    let outgoingReady = false;

    const unsub1 = onSnapshot(incomingQ, (snap) => {
      setIncoming(snap.docs.map((d) => ({ ...(d.data() as any), edgeId: d.id })));
      incomingReady = true;
      if (incomingReady && outgoingReady) setLoading(false);
    });
    const unsub2 = onSnapshot(outgoingQ, (snap) => {
      setOutgoing(snap.docs.map((d) => ({ ...(d.data() as any), edgeId: d.id })));
      outgoingReady = true;
      if (incomingReady && outgoingReady) setLoading(false);
    });

    return () => {
      unsub1();
      unsub2();
    };
  }, [open, uid]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(95vw,680px)] p-0 overflow-hidden border border-gray-800 bg-gray-950">
        <DialogHeader className="px-3 py-2 border-b border-gray-800">
          <DialogTitle className="text-sm text-white">
            Follow requests
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-800">
          {/* Incoming */}
          <section className="p-3">
            <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-2">
              Incoming
            </h3>
            {incoming.length === 0 ? (
              <p className="text-sm text-gray-400">No incoming requests</p>
            ) : (
              <ul className="space-y-2">
                {incoming.map((e) => (
                  <li
                    key={e.edgeId}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-sm text-white truncate">
                      {e.followerId}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        aria-label={`Approve follow from ${e.followerId}`}
                        disabled={busy === e.edgeId}
                        onClick={async () => {
                          setBusy(e.edgeId);
                          try {
                            await approveFollow(e.edgeId);
                            toast({ title: "Approved follow request" });
                          } catch (err: any) {
                            toast({
                              title: "Couldn’t approve",
                              description: err?.message ?? String(err),
                            });
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        {busy === e.edgeId ? "Approving…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Deny follow from ${e.followerId}`}
                        disabled={busy === e.edgeId}
                        onClick={async () => {
                          setBusy(e.edgeId);
                          try {
                            await denyFollow(e.edgeId);
                            toast({ title: "Denied follow request" });
                          } catch (err: any) {
                            toast({
                              title: "Couldn’t deny",
                              description: err?.message ?? String(err),
                            });
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        {busy === e.edgeId ? "Denying…" : "Deny"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Outgoing */}
          <section className="p-3">
            <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-2">
              Outgoing
            </h3>
            {outgoing.length === 0 ? (
              <p className="text-sm text-gray-400">No outgoing requests</p>
            ) : (
              <ul className="space-y-2">
                {outgoing.map((e) => (
                  <li
                    key={e.edgeId}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-sm text-white truncate">
                      {e.followingId}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Cancel follow request to ${e.followingId}`}
                        disabled={busy === e.edgeId}
                        onClick={async () => {
                          setBusy(e.edgeId);
                          try {
                            // denyFollow(edgeId) deletes a pending edge — works for self-cancel
                            await denyFollow(e.edgeId);
                            toast({ title: "Canceled follow request" });
                          } catch (err: any) {
                            toast({
                              title: "Couldn’t cancel",
                              description: err?.message ?? String(err),
                            });
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        {busy === e.edgeId ? "Canceling…" : "Cancel"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

